import { RatingKey } from '@src/components/sco/PitchDeckAuditor/model/types'

/**
 * Presentational metadata for the four rating keys the back-end persists
 * (`pitchDeckRatings`: strong / needsWork / fundamentalRework / notAudited).
 * Colours are intentionally client-side (presentational + stable); the back-end
 * also exposes `colorHexCode` on the static option if a future override is wanted.
 */
export const RATING_META: Record<RatingKey, { label: string; colorPalette: string }> = {
  strong: { label: 'Strong', colorPalette: 'green' },
  needsWork: { label: 'Needs Work', colorPalette: 'orange' },
  fundamentalRework: { label: 'Fundamental Rework', colorPalette: 'red' },
  notAudited: { label: 'Not Audited', colorPalette: 'gray' },
}

const LABEL_TO_KEY: Record<string, RatingKey> = {
  strong: 'strong',
  'needs work': 'needsWork',
  needswork: 'needsWork',
  'fundamental rework': 'fundamentalRework',
  fundamentalrework: 'fundamentalRework',
  'not audited': 'notAudited',
  notaudited: 'notAudited',
}

const VALID_KEYS = new Set<RatingKey>(['strong', 'needsWork', 'fundamentalRework', 'notAudited'])

/** Normalise a back-end ratingKey or display label into a known RatingKey. */
export function normalizeRatingKey(ratingKey?: string | null, ratingLabel?: string | null): RatingKey {
  if (ratingKey && VALID_KEYS.has(ratingKey as RatingKey)) {
    return ratingKey as RatingKey
  }
  const fromLabel = ratingLabel ? LABEL_TO_KEY[ratingLabel.trim().toLowerCase()] : undefined
  return fromLabel ?? 'notAudited'
}

export function ratingLabel(key: RatingKey, fallback?: string | null): string {
  return fallback || RATING_META[key]?.label || RATING_META.notAudited.label
}

export function ratingColorPalette(key: RatingKey): string {
  return RATING_META[key]?.colorPalette || RATING_META.notAudited.colorPalette
}

/** Colour bands for a numeric score ring (ported from the mockup's scoreColorPalette). */
export function scoreColorPalette(score: number | null | undefined): string {
  if (score == null) {
    return 'gray'
  }
  if (score >= 70) {
    return 'green'
  }
  if (score >= 50) {
    return 'orange'
  }
  return 'red'
}

/** Derive a deck-level rating key from the overall score when no explicit rating exists. */
export function ratingKeyFromScore(score: number | null | undefined): RatingKey {
  if (score == null) {
    return 'notAudited'
  }
  if (score >= 70) {
    return 'strong'
  }
  if (score >= 50) {
    return 'needsWork'
  }
  return 'fundamentalRework'
}
